/**
 * ModuleTreeConfig Component
 * Recursive tree-view for configuring tenant module features.
 * Renders from MODULE_TREE data structure with 3-level depth.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Collapse,
  IconButton,
  Button,
  Typography,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MODULE_TREE, MODULE_CATEGORIES } from '../../contexts/PermissionConstants';

/**
 * Collect all leaf paths under a tree node (recursive).
 * A leaf is a node with no children.
 */
const collectLeafPaths = (node, prefix) => {
  if (!node.children) return [prefix];
  const paths = [];
  for (const [key, child] of Object.entries(node.children)) {
    paths.push(...collectLeafPaths(child, `${prefix}.${key}`));
  }
  return paths;
};

/**
 * Collect all paths (including intermediate) under a tree node.
 */
const collectAllPaths = (node, prefix) => {
  if (!node.children) return [prefix];
  const paths = [prefix];
  for (const [key, child] of Object.entries(node.children)) {
    paths.push(...collectAllPaths(child, `${prefix}.${key}`));
  }
  return paths;
};

/**
 * Build the full list of all node paths from MODULE_TREE.
 */
const buildAllNodePaths = () => {
  const paths = [];
  for (const [moduleCode, moduleNode] of Object.entries(MODULE_TREE)) {
    paths.push(...collectAllPaths(moduleNode, moduleCode));
  }
  return paths;
};

const ALL_NODE_PATHS = buildAllNodePaths();

/**
 * Determine the check state of a node based on its children.
 * Returns: 'checked', 'unchecked', or 'indeterminate'
 */
const getNodeState = (node, path, featureState) => {
  if (!node.children) {
    return featureState[path] !== false ? 'checked' : 'unchecked';
  }
  const childKeys = Object.keys(node.children);
  let checkedCount = 0;
  let indeterminateCount = 0;
  for (const key of childKeys) {
    const childState = getNodeState(node.children[key], `${path}.${key}`, featureState);
    if (childState === 'checked') checkedCount++;
    else if (childState === 'indeterminate') indeterminateCount++;
  }
  if (checkedCount === childKeys.length) return 'checked';
  if (checkedCount > 0 || indeterminateCount > 0) return 'indeterminate';
  return 'unchecked';
};

/**
 * Single tree node row (recursive)
 */
const TreeNode = ({ nodeKey, node, path, depth, featureState, onToggle, expanded, onToggleExpand }) => {
  const hasChildren = !!node.children;
  const state = getNodeState(node, path, featureState);
  const isExpanded = expanded[path];

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    const newChecked = state !== 'checked';
    onToggle(node, path, newChecked);
  };

  const handleExpandClick = () => {
    if (hasChildren) onToggleExpand(path);
  };

  return (
    <>
      <ListItem
        disablePadding
        sx={{ pl: depth * 3 }}
      >
        <ListItemButton
          onClick={handleExpandClick}
          dense
          sx={{ py: 0.25, borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            {hasChildren ? (
              <IconButton size="small" sx={{ p: 0 }}>
                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
          </ListItemIcon>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Checkbox
              edge="start"
              checked={state === 'checked'}
              indeterminate={state === 'indeterminate'}
              onClick={handleCheckboxClick}
              size="small"
              sx={{ p: 0 }}
            />
          </ListItemIcon>
          <ListItemText
            primary={node.label}
            primaryTypographyProps={{
              variant: depth === 0 ? 'body1' : 'body2',
              fontWeight: depth === 0 ? 600 : (hasChildren ? 500 : 400),
              fontSize: depth === 0 ? '0.875rem' : '0.8125rem',
            }}
          />
          {hasChildren && (
            <Typography variant="caption" color="text.disabled" sx={{ mr: 1 }}>
              {(() => {
                const leafPaths = collectLeafPaths(node, path);
                const enabledCount = leafPaths.filter(p => featureState[p] !== false).length;
                return `${enabledCount}/${leafPaths.length}`;
              })()}
            </Typography>
          )}
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {Object.entries(node.children).map(([childKey, childNode]) => (
              <TreeNode
                key={childKey}
                nodeKey={childKey}
                node={childNode}
                path={`${path}.${childKey}`}
                depth={depth + 1}
                featureState={featureState}
                onToggle={onToggle}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/**
 * ModuleTreeConfig
 * Props:
 *  - featureState: { "module.category.feature": boolean } flat map
 *  - onChange: (newState) => void
 */
const ModuleTreeConfig = ({ featureState, onChange }) => {
  const [expanded, setExpanded] = useState(() => {
    // Start with top-level modules collapsed
    const initial = {};
    for (const moduleCode of Object.keys(MODULE_TREE)) {
      initial[moduleCode] = false;
    }
    return initial;
  });

  const handleToggleExpand = useCallback((path) => {
    setExpanded(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const handleExpandAll = useCallback(() => {
    const allExpanded = {};
    for (const path of ALL_NODE_PATHS) {
      allExpanded[path] = true;
    }
    setExpanded(allExpanded);
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpanded({});
  }, []);

  const handleToggle = useCallback((node, path, checked) => {
    const newState = { ...featureState };
    // Set this node and all descendants
    const allPaths = collectAllPaths(node, path);
    for (const p of allPaths) {
      newState[p] = checked;
    }
    onChange(newState);
  }, [featureState, onChange]);

  const handleSelectAll = useCallback(() => {
    const newState = { ...featureState };
    for (const path of ALL_NODE_PATHS) {
      newState[path] = true;
    }
    onChange(newState);
  }, [featureState, onChange]);

  const handleClearAll = useCallback(() => {
    const newState = { ...featureState };
    for (const path of ALL_NODE_PATHS) {
      newState[path] = false;
    }
    onChange(newState);
  }, [featureState, onChange]);

  // Group modules by category
  const categories = useMemo(() => {
    return Object.entries(MODULE_CATEGORIES).map(([catKey, moduleCodes]) => ({
      key: catKey,
      label: catKey === 'business' ? 'Business Modules' : 'Administration',
      modules: moduleCodes.filter(code => MODULE_TREE[code]),
    }));
  }, []);

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="text" onClick={handleExpandAll}>
            Expand All
          </Button>
          <Button size="small" variant="text" onClick={handleCollapseAll}>
            Collapse All
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="text" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" variant="text" color="inherit" onClick={handleClearAll}>
            Clear All
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {categories.map((cat) => (
        <Box key={cat.key} sx={{ mb: 1.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5, letterSpacing: '0.08em', fontSize: '0.7rem', px: 1 }}
          >
            {cat.label}
          </Typography>
          <List disablePadding dense>
            {cat.modules.map((moduleCode) => (
              <TreeNode
                key={moduleCode}
                nodeKey={moduleCode}
                node={MODULE_TREE[moduleCode]}
                path={moduleCode}
                depth={0}
                featureState={featureState}
                onToggle={handleToggle}
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default ModuleTreeConfig;
